import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import useLocalStorage from '../hooks/useLocalStorage';
import { KEYS } from '../utils/storage';
import { useToast } from '../components/common/Toast';
import { getCurrentAcademicYear } from '../utils/academicYear';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const toast = useToast();
  const [loading, setLoading]     = useState(true);
  const [apiReady, setApiReady]   = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [academicYear, setAcademicYearState] = useState(getCurrentAcademicYear());
  const [admissionFee, setAdmissionFeeState] = useState(0);
  const academicYearRef = useRef(getCurrentAcademicYear());

  const [students,      setStudents]      = useState([]);
  const [parents,       setParents]       = useState([]);
  const [staff,         setStaff]         = useState([]);
  const [classes,       setClasses]       = useState([]);
  const [subjects,      setSubjects]      = useState([]);
  const [timetable,     setTimetable]     = useState([]);
  const [exams,         setExams]         = useState([]);
  const [marks,         setMarks]         = useState([]);
  const [attendance,    setAttendance]    = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [feePayments,   setFeePayments]   = useState([]);
  const [books,         setBooks]         = useState([]);
  const [bookIssues,    setBookIssues]    = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [homework,      setHomework]      = useState([]);
  const [examTimetable, setExamTimetable] = useState([]);
  const [lessonPlans,   setLessonPlans]   = useState([]);
  const [enquiries,     setEnquiries]     = useState([]);
  const [dropouts,      setDropouts]      = useState([]);

  const EMPTY_TEACHER_MAPPING = { isClassTeacher: false, classId: '', assignedClassIds: [], assignedSubjectIds: [], classSubjectPairs: [] };
  const [teacherMapping, setTeacherMapping] = useState(EMPTY_TEACHER_MAPPING);

  // Local-only (no backend table)
  const [routes,          setRoutes]          = useLocalStorage(KEYS.ROUTES,           []);
  const [vehicles,        setVehicles]        = useLocalStorage(KEYS.VEHICLES,         []);
  const [notifications,   setNotifications]   = useLocalStorage(KEYS.NOTIFICATIONS,    []);
  const [hwCompletions,   setHwCompletions]   = useLocalStorage(KEYS.HW_COMPLETIONS,   []);
  const [questionPapers,  setQuestionPapers]  = useLocalStorage(KEYS.QUESTION_PAPERS,  []);
  const [transportTerms,  setTransportTermsState]  = useState([]);
  const [schoolName,      setSchoolName]      = useState('');
  const [brandLogo,       setBrandLogo]       = useState('');
  const [brandPrimary,    setBrandPrimary]    = useState('#6b21a8');
  const [brandSecondary,  setBrandSecondary]  = useState('#9333ea');

  useEffect(() => { academicYearRef.current = academicYear; }, [academicYear]);

  // Year-filterable tables — must match backend YEAR_FILTERABLE set
  const YEAR_TABLES = new Set(['exams','fee-structures','fee-payments','attendance','timetable','lesson-plans','marks']);

  const safe = (name) => {
    const yr = academicYearRef.current;
    const url = YEAR_TABLES.has(name) && yr ? `${name}?academicYear=${encodeURIComponent(yr)}` : name;
    return api.list(url).catch(() => []);
  };

  const yr = () => academicYearRef.current;

  const setAcademicYear = useCallback(async (year) => {
    setAcademicYearState(year);
    academicYearRef.current = year;
    // Save to settings.json AND school_settings DB (so backend fee/due logic uses correct year)
    try { await api.create('settings', { academicYear: year }); } catch {}
    try { await api.saveSchoolSettings({ academicYear: year }); } catch {}
    // Reload year-dependent data with new filter
    const reload = async (name, setter) => {
      try {
        const url = `${name}?academicYear=${encodeURIComponent(year)}`;
        setter(await api.list(url));
      } catch {}
    };
    reload('exams',          setExams);
    reload('fee-structures', setFeeStructures);
    reload('fee-payments',   setFeePayments);
    reload('attendance',     setAttendance);
    reload('timetable',      setTimetable);
    reload('lesson-plans',   setLessonPlans);
    reload('marks',          setMarks);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setAdmissionFee = useCallback(async (amount) => {
    const val = Number(amount) || 0;
    setAdmissionFeeState(val);
    try { await api.create('settings', { admissionFee: val }); } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Load saved academic year first so safe() uses the correct filter
      try {
        const saved = await api.list('settings');
        if (saved?.academicYear) {
          setAcademicYearState(saved.academicYear);
          academicYearRef.current = saved.academicYear;
        }
      } catch {}

      const [s, p, st, cl, sub, tt, ex, mk, att, fs, fp, bk, bi, ann, hw, et, lp, enq, dr, veh, rts] =
        await Promise.all([
          safe('students'),      safe('parents'),        safe('staff'),
          safe('classes'),       safe('subjects'),       safe('timetable'),
          safe('exams'),         safe('marks'),          safe('attendance'),
          safe('fee-structures'),safe('fee-payments'),
          safe('books'),         safe('book-issues'),    safe('announcements'),
          safe('homework'),      safe('exam-timetable'), safe('lesson-plans'),
          safe('enquiries'),     safe('dropouts'),
          safe('transport/vehicles'), safe('transport/routes'),
        ]);
      setStudents(s);       setParents(p);        setStaff(st);
      setClasses(cl);       setSubjects(sub);     setTimetable(tt);
      setExams(ex);         setMarks(mk);         setAttendance(att);
      setFeeStructures(fs); setFeePayments(fp);
      setBooks(bk);         setBookIssues(bi);
      setAnnouncements(ann);setHomework(hw);
      setExamTimetable(et); setLessonPlans(lp);   setEnquiries(enq);
      setDropouts(dr);
      if (Array.isArray(veh)) setVehicles(veh);
      if (Array.isArray(rts)) setRoutes(rts);
      // Load school branding
      api.getSchoolSettings().then(d => {
        if (d?.name) setSchoolName(d.name);
        if (d?.logo) setBrandLogo(d.logo);
        const primary   = d?.primaryColor   || '#6b21a8';
        const secondary = d?.secondaryColor || '#9333ea';
        setBrandPrimary(primary);
        setBrandSecondary(secondary);
        document.documentElement.style.setProperty('--brand-primary',   primary);
        document.documentElement.style.setProperty('--brand-secondary', secondary);
        // Restore saved academic year from DB (overrides auto-detect)
        if (d?.academicYear) {
          setAcademicYearState(d.academicYear);
          academicYearRef.current = d.academicYear;
        }
      }).catch(() => {});
      // Load transport fee terms from DB
      api.getTransportTerms().then(d => { if (Array.isArray(d)) setTransportTermsState(d); }).catch(() => {});
      setApiReady(true);
    } catch (e) {
      setLoadError(e.message);
      setApiReady(false);
    }
    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTeacherMapping = useCallback(async () => {
    try {
      const m = await api.getTeacherMapping();
      setTeacherMapping(m);
    } catch { setTeacherMapping(EMPTY_TEACHER_MAPPING); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Saves transport terms to DB and updates local state; returns true/false
  const setTransportTerms = useCallback(async (payload) => {
    try {
      const res = await api.saveTransportTerms(payload);
      setTransportTermsState(res.terms || payload);
      return true;
    } catch { return false; }
  }, []);

  // Reload whenever the auth token changes (e.g. after login)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'sms_auth') loadAll();
    };
    window.addEventListener('storage', onStorage);
    loadAll();
    return () => window.removeEventListener('storage', onStorage);
  }, [loadAll]);

  // ── Direct API save helpers ───────────────────────────────────
  // Each function calls the API directly, then updates local state on success.
  // If the API call fails, state is NOT updated and an error toast is shown.

  const apiCreate = useCallback(async (entity, item, setter) => {
    try {
      await api.create(entity, item);
      setter(prev => [...prev, item]);
      toast?.success('Saved successfully');
      return true;
    } catch (e) {
      toast?.error(`Save failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  const apiUpdate = useCallback(async (entity, id, item, setter) => {
    try {
      await api.update(entity, id, item);
      setter(prev => prev.map(x => x.id === id ? item : x));
      toast?.success('Updated successfully');
      return true;
    } catch (e) {
      toast?.error(`Update failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  const apiDelete = useCallback(async (entity, id, setter) => {
    try {
      await api.remove(entity, id);
      setter(prev => prev.filter(x => x.id !== id));
      toast?.success('Deleted');
      return true;
    } catch (e) {
      toast?.error(`Delete failed: ${e.message}`);
      return false;
    }
  }, [toast]);

  // ── Entity-specific save/update/delete ────────────────────────
  const saveClass     = (item)    => apiCreate('classes',        item, setClasses);
  const updateClass   = (id, item)=> apiUpdate('classes',        id, item, setClasses);
  const deleteClass   = (id)      => apiDelete('classes',        id, setClasses);

  const saveStudent   = (item)    => apiCreate('students',       item, setStudents);
  const updateStudent = (id, item)=> apiUpdate('students',       id, item, setStudents);
  const deleteStudent = (id)      => apiDelete('students',       id, setStudents);

  const saveStaff     = (item)    => apiCreate('staff',          item, setStaff);
  const updateStaff   = (id, item)=> apiUpdate('staff',          id, item, setStaff);
  const deleteStaff   = (id)      => apiDelete('staff',          id, setStaff);

  const saveSubject   = (item)    => apiCreate('subjects',       item, setSubjects);
  const updateSubject = (id, item)=> apiUpdate('subjects',       id, item, setSubjects);
  const deleteSubject = (id)      => apiDelete('subjects',       id, setSubjects);

  const saveParent    = (item)    => apiCreate('parents',        item, setParents);
  const updateParent  = (id, item)=> apiUpdate('parents',        id, item, setParents);

  const saveExam      = (item)    => apiCreate('exams',          item, setExams);
  const updateExam    = (id, item)=> apiUpdate('exams',          id, item, setExams);
  const deleteExam    = (id)      => apiDelete('exams',          id, setExams);

  const saveAnnouncement   = (item)    => apiCreate('announcements',  item, setAnnouncements);
  const updateAnnouncement = (id, item)=> apiUpdate('announcements',  id, item, setAnnouncements);
  const deleteAnnouncement = (id)      => apiDelete('announcements',  id, setAnnouncements);

  const saveBook      = (item)    => apiCreate('books',          item, setBooks);
  const updateBook    = (id, item)=> apiUpdate('books',          id, item, setBooks);
  const deleteBook    = (id)      => apiDelete('books',          id, setBooks);

  const saveBookIssue = (item)    => apiCreate('book-issues',    item, setBookIssues);
  const updateBookIssue=(id,item) => apiUpdate('book-issues',    id, item, setBookIssues);

  const saveFeeStructure   = (item)    => apiCreate('fee-structures', item, setFeeStructures);
  const updateFeeStructure = (id, item)=> apiUpdate('fee-structures', id, item, setFeeStructures);

  const saveFeePayment     = (item)    => apiCreate('fee-payments',   item, setFeePayments);
  const updateFeePayment   = (id, item)=> apiUpdate('fee-payments',   id, item, setFeePayments);
  const deleteFeePayment   = (id)      => apiDelete('fee-payments',   id, setFeePayments);

  const saveAttendance     = (item)    => apiCreate('attendance',     item, setAttendance);
  const updateAttendance   = (id, item)=> apiUpdate('attendance',     id, item, setAttendance);

  const saveHomework       = (item)    => apiCreate('homework',       item, setHomework);
  const updateHomework     = (id, item)=> apiUpdate('homework',       id, item, setHomework);
  const deleteHomework     = (id)      => apiDelete('homework',       id, setHomework);

  const saveTimetable      = (item)    => apiCreate('timetable',      item, setTimetable);
  const updateTimetable    = (id, item)=> apiUpdate('timetable',      id, item, setTimetable);
  const deleteTimetable    = (id)      => apiDelete('timetable',      id, setTimetable);

  const saveExamTimetable  = (item)    => apiCreate('exam-timetable', item, setExamTimetable);
  const updateExamTimetable= (id, item)=> apiUpdate('exam-timetable', id, item, setExamTimetable);

  const saveMarks          = (item)    => apiCreate('marks',          item, setMarks);
  const updateMarks        = (id, item)=> apiUpdate('marks',          id, item, setMarks);

  const saveLessonPlan     = (item)    => apiCreate('lesson-plans',   item, setLessonPlans);
  const updateLessonPlan   = (id, item)=> apiUpdate('lesson-plans',   id, item, setLessonPlans);
  const deleteLessonPlan   = (id)      => apiDelete('lesson-plans',   id, setLessonPlans);

  const saveEnquiry        = (item)    => apiCreate('enquiries',      item, setEnquiries);
  const updateEnquiry      = (id, item)=> apiUpdate('enquiries',      id, item, setEnquiries);
  const deleteEnquiry      = (id)      => apiDelete('enquiries',      id, setEnquiries);

  const saveDropout        = (item)    => apiCreate('dropouts',       item, setDropouts);
  const deleteDropout      = (id)      => apiDelete('dropouts',       id, setDropouts);

  const saveVehicle        = (item)    => apiCreate('transport/vehicles', item, setVehicles);
  const updateVehicle      = (id, item)=> apiUpdate('transport/vehicles', id, item, setVehicles);
  const deleteVehicle      = (id)      => apiDelete('transport/vehicles', id, setVehicles);

  const saveRoute          = (item)    => apiCreate('transport/routes',   item, setRoutes);
  const updateRoute        = (id, item)=> apiUpdate('transport/routes',   id, item, setRoutes);
  const deleteRoute        = (id)      => apiDelete('transport/routes',   id, setRoutes);

  return (
    <AppContext.Provider value={{
      loading, apiReady, loadError, reloadData: loadAll,
      academicYear, setAcademicYear,
      admissionFee, setAdmissionFee,
      teacherMapping, loadTeacherMapping,

      students,      setStudents,
      parents,       setParents,
      staff,         setStaff,
      classes,       setClasses,
      subjects,      setSubjects,
      timetable,     setTimetable,
      exams,         setExams,
      marks,         setMarks,
      attendance,    setAttendance,
      feeStructures, setFeeStructures,
      feePayments,   setFeePayments,
      books,         setBooks,
      bookIssues,    setBookIssues,
      announcements, setAnnouncements,
      homework,      setHomework,
      examTimetable, setExamTimetable,
      lessonPlans,   setLessonPlans,
      enquiries,     setEnquiries,

      routes,         setRoutes,
      vehicles,       setVehicles,
      notifications,  setNotifications,
      hwCompletions,  setHwCompletions,
      questionPapers, setQuestionPapers,
      transportTerms, setTransportTerms, setTransportTermsState,
      schoolName,     setSchoolName,
      brandLogo,      setBrandLogo,
      brandPrimary,   setBrandPrimary,
      brandSecondary, setBrandSecondary,

      // Direct API save/update/delete (use these in forms)
      saveClass,     updateClass,     deleteClass,
      saveStudent,   updateStudent,   deleteStudent,
      saveStaff,     updateStaff,     deleteStaff,
      saveSubject,   updateSubject,   deleteSubject,
      saveParent,    updateParent,
      saveExam,      updateExam,      deleteExam,
      saveAnnouncement, updateAnnouncement, deleteAnnouncement,
      saveBook,      updateBook,      deleteBook,
      saveBookIssue, updateBookIssue,
      saveFeeStructure, updateFeeStructure,
      saveFeePayment,   updateFeePayment,  deleteFeePayment,
      saveAttendance,   updateAttendance,
      saveHomework,  updateHomework,  deleteHomework,
      saveTimetable, updateTimetable, deleteTimetable,
      saveExamTimetable, updateExamTimetable,
      saveMarks,     updateMarks,
      saveLessonPlan, updateLessonPlan, deleteLessonPlan,
      saveEnquiry,   updateEnquiry,   deleteEnquiry,
      dropouts,      setDropouts,     saveDropout,     deleteDropout,
      saveVehicle,   updateVehicle,   deleteVehicle,
      saveRoute,     updateRoute,     deleteRoute,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
